import React, { useContext, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import { CajasContext } from "src/context/CajasContext/CajasContext";
import esArray from "src/general_components/functionEsArray";
import { getDataSelect } from "src/hooks/structureTable";
import { TextField, Autocomplete } from "@mui/material";

export const FormUbicaciones = ({ data, setData, valorUbicacionTemporal }) => {
  const {
    setListDatosFormUbicaciones,
    formUbicacionesDisabled,
    setFormUbicacionesDisabled,
    setDataSearchFormUbicaciones,
    setGrupoBusquedaUbicaciones,
    setSnackbarVisible,
    setMessage,
    setSeverity,
  } = useContext(CajasContext);

  const [depositos, setDepositos] = useState([]);

  // Estado para valores de coordenadas
  const [valoresCoordenadas, setValoresCoordenadas] = useState({
    deposito: data.deposito || [],
    sector: data.sector || "",
    modulo: data.modulo || "",
    estante: data.estante || "",
    posicion: data.posicion || "",
    estibe: data.estibe || "",
    profundidad: data.profundidad || "",
    estadoUbicacion: data.estadoUbicacion || null,
    ubicacionTemporal: data.ubicacionTemporal || "",
  });

  // 🔹 Limpia todos los datos del formulario
  const limpiar = () => {
    setData({ idDep: "", disabled: false });
    setListDatosFormUbicaciones({});
    setFormUbicacionesDisabled(false);
    setGrupoBusquedaUbicaciones(false);
    setValoresCoordenadas({
      deposito: [],
      sector: "",
      modulo: "",
      estante: "",
      posicion: "",
      estibe: "",
      profundidad: "",
      estadoUbicacion: null,
      ubicacionTemporal: "",
    });
  };

  // 🔹 Validación numérica (solo > 0)
  const handleNumericChange = (key, value) => {
    const num = Number(value);

    if (isNaN(num) || num <= 0) {
      setSnackbarVisible(true);
      setMessage("Solo se permiten números mayores a 0");
      setSeverity("warning");
      setValoresCoordenadas((prev) => ({ ...prev, [key]: "" }));
      return;
    }

    setValoresCoordenadas((prev) => ({ ...prev, [key]: num }));
  };

  // 🔹 Carga inicial de datos desde localStorage o API
  useEffect(() => {
    const modelos = [{ key: "owner_localizaciones", setter: setDepositos }];

    const cargarDatos = async () => {
      for (const { key, setter } of modelos) {
        let data = esArray(JSON.parse(localStorage.getItem(`siam-${key}`))?.[key]);
        if (!data.length) {
          data = await getDataSelect(key);
        }
        setter(data);
      }
    };

    cargarDatos();
  }, []);

  // 🔹 Actualiza búsqueda cada vez que cambia `data`
  useEffect(() => {
    const datos = {};
    setListDatosFormUbicaciones({});

    if (data.idDep) {
      datos["owner_localizaciones"] = { id_owner_localizaciones: data.idDep };
      const keys = ["sector", "modulo", "estante", "posicion", "profundidad", "estibe"];
      keys.forEach((key) => {
        if (data[key]) {
          datos[key] = { [key]: `${data[key]}` };
        }
      });

      setDataSearchFormUbicaciones(datos);
      setGrupoBusquedaUbicaciones(true);
    }

    if (data.ubicacionTemporal && !data.idDep) {
      datos["ubicacionTemporal"] = { ubicacionTemporal: `${data.ubicacionTemporal}` };
      setDataSearchFormUbicaciones(datos);
    }

    setListDatosFormUbicaciones(data);
  }, [data, setDataSearchFormUbicaciones, setListDatosFormUbicaciones, setGrupoBusquedaUbicaciones]);

  // 🔹 Sincroniza `valoresCoordenadas` → `data` (padre)
  useEffect(() => {
    const dataToSet = { ...valoresCoordenadas };

    if (valoresCoordenadas.deposito && valoresCoordenadas.deposito.length > 0) {
      dataToSet.idDep = valoresCoordenadas.deposito.map((d) => d.id).join(",");
      dataToSet.disabled = true;
    } else {
      dataToSet.idDep = "";
      dataToSet.disabled = false;
    }

    if (valorUbicacionTemporal) {
      dataToSet.ubicacion_temporal = valorUbicacionTemporal;
    }

    setData(dataToSet);
  }, [valoresCoordenadas, valorUbicacionTemporal, setData]);

  // 🔹 Render dinámico de inputs
  const renderCampo = (key, label, options, disabled = false, type = "number") => {
    if (options) {
      return (
        <Autocomplete
          multiple
          options={options}
          value={valoresCoordenadas[key] || []}
          onChange={(_, newValue) =>
            setValoresCoordenadas((prev) => ({ ...prev, [key]: newValue }))
          }
          getOptionLabel={(option) => option?.label || option?.detalle || option?.nombre || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} label={label} variant="outlined" fullWidth disabled={disabled} />
          )}
        />
      );
    }

    if (type === "text") {
      return (
        <TextField
          label={label}
          type="text"
          variant="outlined"
          fullWidth
          disabled={disabled}
          value={valoresCoordenadas[key] || ""}
          onChange={(e) =>
            setValoresCoordenadas((prev) => ({
              ...prev,
              [key]: e.target.value,
            }))
          }
        />
      );
    }

    return (
      <TextField
        label={label}
        type="number"
        variant="outlined"
        fullWidth
        disabled={disabled}
        value={valoresCoordenadas[key] || ""}
        onChange={(e) => handleNumericChange(key, e.target.value)}
        inputProps={{ min: 1 }}
      />
    );
  };
  // 🧱 Render principal
  return (
    <div>
      {/* Botón limpiar */}
      <Grid container justifyContent="end" style={{ marginBottom: "3px" }}>
        <Button
          variant="contained"
          onClick={limpiar}
          color="secondary"
          sx={{ padding: "5px", minWidth: "auto" }}
        >
          <ReplayIcon style={{ padding: "0px", position: "center" }} />
        </Button>
      </Grid>

      {/* Fila 1 */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("deposito", "Depósito", depositos,formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("sector", "Sector", null, formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("modulo", "Módulo", null, formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("estante", "Estante", null, formUbicacionesDisabled)}
        </Grid>
      </Grid>

      {/* Fila 2 */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("posicion", "Posición", null, formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("profundidad", "Profundidad", null, formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("estibe", "Estibe", null, formUbicacionesDisabled)}
        </Grid>
        <Grid item xs={12} sm sx={{ flexBasis: "20%" }}>
          {renderCampo("ubicacionTemporal", "Ubicación Temporal", null, formUbicacionesDisabled, "text")}
        </Grid>
      </Grid>
    </div>
  );
};
